/**
 * Integration Dashboard
 *
 * Monitors integration executions, health metrics, and provides real-time status updates.
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { getApiBaseUrl } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Tooltip } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  Play,
  Eye,
  Loader2,
  Info,
  ArrowLeftRight,
} from 'lucide-react';

interface IntegrationExecution {
  id: number;
  integration_config_id: number;
  execution_mode: string;
  execution_status: string;
  execution_type: string;
  records_fetched: number;
  records_processed: number;
  records_inserted: number;
  records_updated: number;
  records_failed: number;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
}

interface IntegrationLog {
  id: number;
  log_level: string;
  log_type: string;
  message: string;
  details: any;
  logged_at: string;
}

interface IntegrationHealth {
  integration_id: number;
  name: string;
  integration_type: string;
  flow_direction: string;
  total_executions: number;
  successful_executions: number;
  failed_executions: number;
  total_records_processed: number;
  avg_duration_seconds: number;
  last_execution_at?: string;
}

export const IntegrationDashboard: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [executions, setExecutions] = useState<IntegrationExecution[]>([]);
  const [healthMetrics, setHealthMetrics] = useState<IntegrationHealth[]>([]);
  const [selectedExecution, setSelectedExecution] = useState<number | null>(null);
  const [executionLogs, setExecutionLogs] = useState<IntegrationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('executions');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Get headers with CBO ID for API calls
  const getHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'X-CBO-ID': localStorage.getItem('active_cbo_id') || '1'
  });

  // Fetch recent executions
  const fetchExecutions = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/integrations/executions`, {
        headers: getHeaders()
      });

      if (!response.ok) throw new Error('Failed to fetch executions');

      const data = await response.json();
      setExecutions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
      setExecutions([]); // Set empty array on error
    }
  };

  // Fetch health metrics
  const fetchHealthMetrics = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/integrations/health`, {
        headers: getHeaders()
      });

      if (!response.ok) throw new Error('Failed to fetch health metrics');

      const data = await response.json();
      setHealthMetrics(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching health metrics:', err);
      setHealthMetrics([]); // Set empty array on error
    }
  };

  // Fetch execution logs
  const fetchExecutionLogs = async (executionId: number) => {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/integrations/executions/${executionId}/logs`,
        {
          headers: getHeaders()
        }
      );

      if (!response.ok) throw new Error('Failed to fetch logs');

      const data = await response.json();
      setExecutionLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setExecutionLogs([]); // Set empty array on error
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchExecutions(), fetchHealthMetrics()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Auto-refresh - disabled when there are errors to prevent loops
  useEffect(() => {
    if (!autoRefresh || error) return;

    const interval = setInterval(() => {
      fetchExecutions();
      fetchHealthMetrics();
    }, 30000); // Refresh every 30 seconds (reduced frequency)

    return () => clearInterval(interval);
  }, [autoRefresh, error]);

  // View logs
  const handleViewLogs = async (executionId: number) => {
    setSelectedExecution(executionId);
    await fetchExecutionLogs(executionId);
    setLogsDialogOpen(true);
  };

  // Status badge
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: any; icon: any }> = {
      pending: { variant: 'default', icon: <Clock className="w-3 h-3" /> },
      running: { variant: 'info', icon: <Play className="w-3 h-3" /> },
      completed: { variant: 'success', icon: <CheckCircle className="w-3 h-3" /> },
      failed: { variant: 'danger', icon: <AlertCircle className="w-3 h-3" /> },
      partial_success: { variant: 'warning', icon: <AlertCircle className="w-3 h-3" /> }
    };

    const config = statusMap[status] || statusMap.pending;

    return (
      <Badge variant={config.variant} size="sm" className="gap-1">
        {config.icon}
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  // Format duration
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  // Format date/time
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return fmt.dateTime(dateString);
  };

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="space-y-3">
          <Progress value={undefined} className="w-full" />
          <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <p className="text-center">
              {t('integrations.loading_integration_dashboard', 'Loading integration dashboard...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <ArrowLeftRight className="h-6 w-6 text-blue-600" />
          {t('integrations.integration_dashboard', 'Integration Dashboard')}
        </h1>
        <div className="flex gap-2">
          <Tooltip
            content={autoRefresh ? 'Auto-refresh enabled' : 'Auto-refresh disabled'}
            position="bottom"
          >
            <Button
              variant={autoRefresh ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              Auto-Refresh: {autoRefresh ? 'ON' : 'OFF'}
            </Button>
          </Tooltip>
          <Tooltip
            content={t('integrations.refresh_now', 'Refresh now')}
            position="bottom"
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                fetchExecutions();
                fetchHealthMetrics();
              }}
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
          </Tooltip>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs defaultValue={activeTab} className="w-full">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 mb-6">
          <TabsList className="inline-flex h-10 items-center justify-start rounded-md bg-gray-100 dark:bg-gray-800 p-1 text-gray-500 dark:text-gray-400 w-max min-w-full flex-nowrap">
            <TabsTrigger
              value="executions"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=active]:shadow-sm"
            >
              <span className="hidden sm:inline">{t('integrations.recent_executions', 'Recent Executions')}</span>
              <span className="sm:hidden">Executions</span>
            </TabsTrigger>
            <TabsTrigger
              value="health"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-900 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=active]:shadow-sm"
            >
              <span className="hidden sm:inline">{t('integrations.health_metrics', 'Health Metrics')}</span>
              <span className="sm:hidden">Health</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Tab 1: Recent Executions */}
        <TabsContent value="executions" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>
                {t('integrations.recent_integration_executions', 'Recent Integration Executions')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {executions.length === 0 ? (
                <Alert className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <AlertDescription className="text-blue-700 dark:text-blue-300">
                    {t('integrations.no_integration_executions_found_upload', 'No integration executions found. Upload a CSV file or configure an integration to get started.')}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="rounded-md border border-gray-200 dark:border-gray-700">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>{t('integrations.type', 'Type')}</TableHead>
                        <TableHead>{t('integrations.status', 'Status')}</TableHead>
                        <TableHead className="text-right">{t('integrations.fetched', 'Fetched')}</TableHead>
                        <TableHead className="text-right">{t('integrations.processed', 'Processed')}</TableHead>
                        <TableHead className="text-right">{t('integrations.inserted', 'Inserted')}</TableHead>
                        <TableHead className="text-right">{t('integrations.failed', 'Failed')}</TableHead>
                        <TableHead>{t('integrations.started', 'Started')}</TableHead>
                        <TableHead>{t('integrations.duration', 'Duration')}</TableHead>
                        <TableHead>{t('integrations.actions', 'Actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {executions.map((execution) => (
                        <TableRow
                          key={execution.id}
                          className={
                            execution.execution_status === 'running'
                              ? 'bg-blue-50 dark:bg-blue-900/20'
                              : ''
                          }
                        >
                          <TableCell>{execution.id}</TableCell>
                          <TableCell>
                            <Badge variant="outline" size="sm">
                              {execution.execution_type}
                            </Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(execution.execution_status)}</TableCell>
                          <TableCell className="text-right">{execution.records_fetched || 0}</TableCell>
                          <TableCell className="text-right">{execution.records_processed || 0}</TableCell>
                          <TableCell className="text-right">
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              {execution.records_inserted || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-red-600 dark:text-red-400 font-medium">
                              {execution.records_failed || 0}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {formatDateTime(execution.started_at)}
                            </span>
                          </TableCell>
                          <TableCell>{formatDuration(execution.duration_seconds)}</TableCell>
                          <TableCell>
                            <Tooltip
                              content={t('integrations.view_logs', 'View logs')}
                              position="left"
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewLogs(execution.id)}
                                className="h-8 w-8"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Health Metrics */}
        <TabsContent value="health" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {healthMetrics.map((health) => (
              <Card key={health.integration_id}>
                <CardHeader>
                  <CardTitle>{health.name}</CardTitle>
                  <div className="flex gap-2 mt-2">
                    <Badge size="sm" variant="primary">{health.integration_type}</Badge>
                    <Badge size="sm" variant="outline">{health.flow_direction}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t('integrations.total_executions', 'Total Executions')}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {health.total_executions}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t('integrations.success_rate', 'Success Rate')}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {health.total_executions > 0
                          ? Math.round((health.successful_executions / health.total_executions) * 100)
                          : 0}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t('integrations.records_processed', 'Records Processed')}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {health.total_records_processed.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t('integrations.avg_duration', 'Avg Duration')}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {formatDuration(health.avg_duration_seconds)}
                      </p>
                    </div>
                  </div>

                  {health.last_execution_at && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                      Last run: {formatDateTime(health.last_execution_at)}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}

            {healthMetrics.length === 0 && (
              <div className="col-span-2">
                <Alert className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <AlertDescription className="text-blue-700 dark:text-blue-300">
                    {t('integrations.no_integration_health_metrics_available', 'No integration health metrics available yet.')}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Logs Dialog */}
      <Dialog open={logsDialogOpen} onOpenChange={setLogsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Execution Logs - #{selectedExecution}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {executionLogs.length === 0 ? (
              <Alert className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-700 dark:text-blue-300">
                  {t('integrations.no_logs_available_for_this', 'No logs available for this execution.')}
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-3">
                {executionLogs.map((log) => (
                  <div
                    key={log.id}
                    className={`p-4 rounded-lg border ${
                      log.log_level === 'ERROR'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : log.log_level === 'WARNING'
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        size="sm"
                        variant={
                          log.log_level === 'ERROR'
                            ? 'danger'
                            : log.log_level === 'WARNING'
                            ? 'warning'
                            : 'default'
                        }
                      >
                        {log.log_level}
                      </Badge>
                      <Badge size="sm" variant="outline">
                        {log.log_type}
                      </Badge>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDateTime(log.logged_at)}
                      </span>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-900 dark:text-gray-100">{log.message}</p>
                      {log.details && (
                        <pre className="text-xs text-gray-600 dark:text-gray-400 mt-2 overflow-x-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setLogsDialogOpen(false)} variant="outline">
              {t('integrations.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationDashboard;
